import React from "react";
import {Tooltip as MuiTooltip} from "@material-ui/core";
import PropTypes from 'prop-types'
import {makeStyles, useTheme} from "@material-ui/core/styles";
import palette from "../../lib/appfuse-react/core/palette";
import lang from "../../lib/appfuse-react/core/lang";

const useStyles = makeStyles(theme =>({
    tooltip: {
        display: props => props.display,
        backgroundColor: props => props.backgroundColor,
        color: props => props.color,
        boxShadow: props => props.boxShadow,
        padding: props => props.padding,
        height: props => props.height,
        minHeight: props => props.minHeight,
        maxHeight: props => props.maxHeight,
        width: props => props.width,
        minWidth: props => props.minWidth,
        maxWidth: props => props.maxWidth,
        overflow: props => props.overflow,
        borderRadius: props => props.borderRadius,
    }
}))

const Tooltip = React.forwardRef((props, ref) => {
    const {
        children,
        title,
        interactive,
        placement,
        arrow,
        disableFocusListener,
        disableHoverListener,
        disableTouchListener,
        TransitionProps,
        PopperProps,
        color,
        backgroundOpacity,
        height,
        width,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        overflow,
        display,
        open
    } = props;

    const theme = useTheme();
    let backgroundColor = theme.palette.background[props.backgroundColor] || theme.palette[props.backgroundColor]?.main || props.backgroundColor;
    backgroundColor && backgroundOpacity && (backgroundColor = palette.color(backgroundColor, backgroundOpacity))
    const boxShadow = theme.shadows[props.boxShadow];

    const calcSpacing = (spacing) => lang.isNumber(spacing) ? `${theme.spacing(spacing)}px` : spacing;
    const padding = calcSpacing(props.padding);
    const paddingTop = calcSpacing(props.paddingTop || props.paddingY);
    const paddingRight = calcSpacing(props.paddingRight || props.paddingX);
    const paddingBottom = calcSpacing(props.paddingBottom || props.paddingY);
    const paddingLeft = calcSpacing(props.paddingLeft || props.paddingX);

    const calcBorderRadius = (radius) => lang.isNumber(radius)? `${theme.shape.borderRadius * radius}px` : radius;
    const borderRadius = calcBorderRadius(props.borderRadius);

    const classes = useStyles({borderRadius, backgroundColor, color, backgroundOpacity, height, minHeight, maxHeight, width, minWidth,
        maxWidth, padding, paddingTop, paddingRight, paddingBottom, paddingLeft, boxShadow, display, overflow})

    return (
        <MuiTooltip
            ref={ref}
            title={title}
            children={children}
            interactive={interactive}
            placement={placement}
            arrow={arrow}
            open={open}
            disableFocusListener={disableFocusListener}
            disableHoverListener={disableHoverListener}
            disableTouchListener={disableTouchListener}
            TransitionProps={TransitionProps}
            PopperProps={PopperProps}
            onClose={props.onClose}
            classes={{tooltip: classes.tooltip}}
        />
    )
})


Tooltip.propTypes = {
    children: PropTypes.element,
    title: PropTypes.node,
    interactive: PropTypes.bool,
    placement: PropTypes.oneOf(['bottom-end' ,'bottom-start'
        ,'bottom','left-end','left-start','left','right-end','right-start'
        ,'right','top-end','top-start','top']),
    arrow: PropTypes.bool,
    disableFocusListener: PropTypes.bool,
    disableHoverListener: PropTypes.bool,
    disableTouchListener: PropTypes.bool,
    TransitionProps: PropTypes.object,

    display: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]),
    overflow: PropTypes.string,

    color: PropTypes.string,
    backgroundColor: PropTypes.string,
    opacity: PropTypes.number,
    boxShadow: PropTypes.number,
    // Border
    borderRadius: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),

    // Spacing
    padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingX: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingY: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingTop: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingRight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingBottom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingLeft: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),

    // Sizing
    width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    maxWidth: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    minWidth: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    maxHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    minHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
}

Tooltip.defaultProps = {
    interactive: true,
    arrow: true,
    placement: 'left-start',
    color: 'inherit',
    TransitionProps: {timeout: 200}
}

export default Tooltip
